import React from 'react'

import clsx from 'clsx'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import Grid from 'components/Grid'
import Icon from 'components/Icon'
import Rating from 'components/Rating'
import ReviewMethodologyModal from 'components/ReviewMethodologyModal'
import Typography from 'components/Typography'
import useViewport from 'hooks/useViewport'

import ReviewsSnippet from './components/ReviewsSnippet'
import SupportOptions from './components/SupportOptions'

import classes from './CustomerSupportSummary.module.scss'

const SWIPER_BREAKPOINTS_CONFIG = {
  360: {
    slidesPerView: 1,
    centeredSlides: false,
    spaceBetween: 14,
  },
}

/** @type {import("./types").default} */
const CustomerSupportSummary = ({ listing, className = '' }) => {
  const summary = listing?.customer_support_summary
  const snippets = [...summary.pros.snippets, ...summary.cons.snippets]
  const hasSnippets = snippets.length > 0
  const { is, up, down } = useViewport()
  const supportRating = listing.reviews?.by_field?.rating_support || 0
  const methodologyCopy = summary.num_review
    ? `We analyzed ${summary.num_review} verified user reviews to identify positive and negative aspects of ${listing.name} customer support.`
    : `We analyzed verified user reviews to identify positive and negative aspects of ${listing.name} customer support.`

  const renderProsSummary = summary.pros.summary?.map((pro, index) => {
    return (
      <div className={classes.prosConsContainer} key={index}>
        <Icon size={16} className={clsx(classes.iconPros, classes.icon)} icon="caret-circle-up" />
        <Typography variant={up('lg') ? 'body-xs' : 'body-sm'}>{pro}</Typography>
      </div>
    )
  })

  const renderConsSummary = summary.cons.summary?.map((con, index) => {
    return (
      <div className={classes.prosConsContainer} key={index}>
        <Icon size={16} className={clsx(classes.iconCons, classes.icon)} icon="caret-circle-down" />
        <Typography variant={up('lg') ? 'body-xs' : 'body-sm'}>{con}</Typography>
      </div>
    )
  })

  const renderSnippets = snippets.map((snippet, index) => {
    return (
      <Grid item xs={12} sm={12} lg={4} className={classes.snippetSection} key={index}>
        {is('xs') ? (
          <SwiperSlide key={index}>
            <ReviewsSnippet snippet={snippet} type={snippet.type} />
          </SwiperSlide>
        ) : (
          <ReviewsSnippet snippet={snippet} type={snippet.type} />
        )}
      </Grid>
    )
  })

  return (
    <Grid className={clsx('CustomerSupportSummary', classes.root, className)}>
      <div className={classes.titleContainer}>
        <Typography component="h2" className={classes.title}>
          {`${listing.name} customer support`}
        </Typography>
      </div>

      <Grid container>
        <Grid item lg={8} xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <div className={classes.customerSupportContainer}>
                <Typography component="h3" className={classes.subTitle}>
                  {`What do users say about ${listing.name} customer support?`}
                </Typography>
              </div>

              <div className={classes.customerSupportContainer}>
                <Typography variant="heading-medium" className={classes.customerSupport}>
                  Customer support rating:
                </Typography>
                <Rating.Link
                  average={supportRating.toString()}
                  totals={listing.num_reviews}
                  isCompact
                  size="small"
                  hideTotals={true}
                />
              </div>

              <Typography>
                {methodologyCopy} <ReviewMethodologyModal targetComponent="product_customer-support" />
              </Typography>

              <Grid item xs={12}>
                {summary.pros.summary && summary.pros.summary.length > 0 && renderProsSummary}
                {summary.cons.summary && summary.cons.summary.length > 0 && renderConsSummary}
              </Grid>

              {hasSnippets && down('md') && (
                <Grid item xs={12} className={classes.spacingTop}>
                  <Typography>
                    {`To see what individual users say about ${listing.name}\'s customer support, check out the review snippets below.`}
                  </Typography>

                  {is('xs') ? (
                    <div className={classes.snippetRoot}>
                      <Swiper
                        modules={[Pagination]}
                        pagination={{ clickable: true, el: '#cr-customer-support-pagination' }}
                        breakpoints={SWIPER_BREAKPOINTS_CONFIG}
                        simulateTouch={false}
                      >
                        {renderSnippets}
                      </Swiper>
                      <div id="cr-customer-support-pagination" className={classes.pagination} />
                    </div>
                  ) : (
                    renderSnippets
                  )}
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item lg={4} xs={12} className={classes.supportOptionsContainer}>
          <SupportOptions listing={listing} />
        </Grid>
      </Grid>

      {hasSnippets && up('lg') && (
        <Grid item lg={12} className={classes.spacingTop}>
          <Typography>
            {`To see what individual users say about ${listing.name}\'s customer support, check out the review snippets below.`}
          </Typography>

          <Grid container className={classes.reviewsSnippetContainer}>
            {renderSnippets}
          </Grid>
        </Grid>
      )}
    </Grid>
  )
}

export default CustomerSupportSummary
